import clsx from "clsx";
import { EditIcon } from "lucide-react";

import { CompanyProfileSectionTitle } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { Company, CompanyInformation } from "@/types";

const CompanyDetail = ({ title, value }: { title: string; value: string }) => {
  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium text-slate-700">{title}</p>
      <p className="text-sm text-slate-700">{value}</p>
    </div>
  );
};

export const CompanyProfileInformation = ({
  className,
  company,
  companyInformation,
  setIsEditing,
}: {
  className?: string;
  company: Company;
  companyInformation: CompanyInformation | null;
  setIsEditing: (isEditing: boolean) => void;
}) => {
  return (
    <div className={clsx("flex flex-col gap-8", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {companyInformation?.companyInformation.logoUrl && (
            <img
              alt={`${company.name} logo`}
              className="h-20 w-20"
              src={companyInformation.companyInformation.logoUrl}
            />
          )}
          <div>
            <p className="text-2xl font-semibold text-slate-700">
              {company.name}
            </p>
            {companyInformation?.companyInformation.address && (
              <p className="text-sm font-medium text-slate-700">
                {companyInformation.companyInformation.address}
              </p>
            )}
          </div>
        </div>
        <Button
          className="text-primary-900 hover:text-primary-900"
          onClick={() => {
            setIsEditing(true);
          }}
          variant="ghost"
        >
          <EditIcon className="h-4 w-4" /> Edit
        </Button>
      </div>

      {/* About Section */}
      {companyInformation?.companyInformation.description && (
        <div className="flex flex-col gap-4">
          <CompanyProfileSectionTitle title="About" />
          <p className="font-medium text-slate-700">
            {companyInformation.companyInformation.description}
          </p>
        </div>
      )}

      {/* NAICS Code(s) Section */}
      {companyInformation?.companyInformation.naicsCodes &&
        companyInformation.companyInformation.naicsCodes.length > 0 && (
          <div className="flex flex-col gap-2">
            <CompanyProfileSectionTitle title="NAICS Code(s)" />
            <div className="flex flex-wrap gap-1">
              {companyInformation.companyInformation.naicsCodes.map(
                (naicsCode: string) => (
                  <Badge
                    className="bg-slate-50"
                    key={naicsCode}
                    variant="outline"
                  >
                    {naicsCode}
                  </Badge>
                ),
              )}
            </div>
          </div>
        )}

      {/* Customer Industries Section */}
      {companyInformation?.companyInformation.customerIndustries &&
        companyInformation.companyInformation.customerIndustries.length > 0 && (
          <div className="flex flex-col gap-2">
            <CompanyProfileSectionTitle title="Industries of Our Customers" />
            <div className="flex flex-wrap gap-1">
              {companyInformation.companyInformation.customerIndustries.map(
                (customerIndustry: string) => (
                  <Badge
                    className="bg-slate-50"
                    key={customerIndustry}
                    variant="outline"
                  >
                    {customerIndustry}
                  </Badge>
                ),
              )}
            </div>
          </div>
        )}

      {/* Manufacturing Capabilities / Products Section */}
      {companyInformation?.companyInformation.manufacturingCapabilities &&
        companyInformation.companyInformation.manufacturingCapabilities.length >
          0 && (
          <div className="flex flex-col gap-2">
            <CompanyProfileSectionTitle title="Manufacturing Capabilities / Products" />
            <div className="flex flex-wrap gap-1">
              {companyInformation.companyInformation.manufacturingCapabilities.map(
                (manufacturingCapability: string) => (
                  <Badge
                    className="bg-slate-50"
                    key={manufacturingCapability}
                    variant="outline"
                  >
                    {manufacturingCapability}
                  </Badge>
                ),
              )}
            </div>
          </div>
        )}

      {/* Equipment Section */}
      {companyInformation?.companyInformation.equipment &&
        companyInformation.companyInformation.equipment.length > 0 && (
          <div className="flex flex-col gap-2">
            <CompanyProfileSectionTitle title="Equipment" />
            <div className="flex flex-wrap gap-1">
              {companyInformation.companyInformation.equipment.map(
                (e: string) => (
                  <Badge className="bg-slate-50" key={e} variant="outline">
                    {e}
                  </Badge>
                ),
              )}
            </div>
          </div>
        )}

      {/* Materials Section */}
      {companyInformation?.companyInformation.materials &&
        companyInformation.companyInformation.materials.length > 0 && (
          <div className="flex flex-col gap-2">
            <CompanyProfileSectionTitle title="Materials" />
            <div className="flex flex-wrap gap-1">
              {companyInformation.companyInformation.materials.map(
                (material: string) => (
                  <Badge
                    className="bg-slate-50"
                    key={material}
                    variant="outline"
                  >
                    {material}
                  </Badge>
                ),
              )}
            </div>
          </div>
        )}

      {/* Company Details Section */}
      {(companyInformation?.companyInformation.headcount ??
        companyInformation?.companyInformation.foundedYear ??
        companyInformation?.companyInformation.location ??
        companyInformation?.companyInformation.dunsNumber ??
        companyInformation?.companyInformation.cageCode) && (
        <div className="flex flex-col gap-2">
          <CompanyProfileSectionTitle title="Company Details" />
          <div className="grid w-[83%] grid-cols-3 gap-4">
            {companyInformation.companyInformation.headcount && (
              <CompanyDetail
                title="Headcount"
                value={companyInformation.companyInformation.headcount.toLocaleString()}
              />
            )}
            {companyInformation.companyInformation.foundedYear && (
              <CompanyDetail
                title="Founded In"
                value={companyInformation.companyInformation.foundedYear}
              />
            )}
            {companyInformation.companyInformation.location && (
              <CompanyDetail
                title="Location"
                value={companyInformation.companyInformation.location}
              />
            )}
            {companyInformation.companyInformation.dunsNumber && (
              <CompanyDetail
                title="D-U-N-S Number"
                value={companyInformation.companyInformation.dunsNumber}
              />
            )}
            {companyInformation.companyInformation.cageCode && (
              <CompanyDetail
                title="Cage Code"
                value={companyInformation.companyInformation.cageCode}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
